import { useNavigate, useParams } from '@tanstack/react-router'
import { useSearch } from '@tanstack/react-router'
import JSONCrush from 'jsoncrush'
import { useCallback } from 'react'
import { isPlainObject } from 'remeda'

import { AnalyticsQueryFilter } from '@hap/analytics'
import { Timespan } from '@hap/utils'

import { getCrushedParam, parseCrushedParam } from './useCrushedParam'
import useTimespanPreference from './useTimespanPreference'

interface NavigateOptions {
  replace?: boolean
}

interface Path {
  pathname: string
  search?: string
  hash?: string
}

interface Return {
  buildPath: (to: unknown) => string
  buildDrilldownPath: (to: unknown) => string
  navigateTo: (to: unknown, options?: NavigateOptions) => void
  drilldownTo: (to: unknown) => void
  redirectTo: (to: unknown) => void
  buildDrilldownState: (
    to: Partial<Path> | string,
    query?: AnalyticsQueryFilter[],
    timespan?: Timespan.Info,
  ) => string | Partial<Path>
}

const useNavigation = (): Return => {
  const navigate = useNavigate()
  const params = useParams({ strict: false })
  const search = useSearch({ strict: false })
  const userTimespan = useTimespanPreference()
  const buildPath = useCallback(
    (to: unknown): string => {
      const matcher = new RegExp(`^/${params.alias}/`)
      return typeof to === 'string' && (!/^\//.test(to) || matcher.test(to))
        ? to
        : `/${params.alias}${to}`
    },
    [params],
  )

  const buildDrilldownPath = useCallback(
    (to: unknown): string => {
      const query = new URLSearchParams(window.location.search)
      const filters = parseCrushedParam(getCrushedParam())

      if (filters) {
        query.set('r', JSONCrush.crush(JSON.stringify(filters)))
      }
      return buildPath(to + `?${query.toString()}`)
    },
    [buildPath, params],
  )

  const buildDrilldownState = useCallback(
    (
      to: string | Partial<Path>,
      overwrites: AnalyticsQueryFilter[] = [],
      timespan?: Timespan.Info,
    ): string | Partial<Path> => {
      if (typeof to !== 'string') return to
      const [path, searchParams] = to.split('?', 2)
      const q = new URLSearchParams(searchParams)
      const filters = parseCrushedParam(getCrushedParam())
      const query: unknown[] = []

      if (filters) {
        if (filters.cId)
          query.push({
            field: 'channelId',
            op: 'in',
            values: filters.cId,
            dataset: 'interactions',
          })
        if (filters.tId)
          query.push({
            field: 'tagIds',
            op: 'in',
            values: filters.tId,
            dataset: 'interactions',
          })
        if (filters.uId)
          query.push({
            field: 'handledByIds',
            op: 'in',
            values: filters.uId,
            dataset: 'interactions',
          })
      }

      // These are used on the dashboard
      if (search.filters) {
        if ('tagIds' in search.filters && isPlainObject(search.filters.tagIds)) {
          query.push({ ...search.filters.tagIds, field: 'tagIds' })
        }
        if ('channelIds' in search.filters && isPlainObject(search.filters.channelIds)) {
          query.push({ ...search.filters.channelIds, field: 'channelId' })
        }
        if ('groupIds' in search.filters && isPlainObject(search.filters.groupIds)) {
          query.push({ ...search.filters.groupIds, field: 'groupIds' })
        }
        if ('handleTime' in search.filters && isPlainObject(search.filters.handleTime)) {
          query.push({ ...search.filters.handleTime, field: 'handleTime' })
        }
        if (
          'reportsToIds' in search.filters &&
          isPlainObject(search.filters.reportsToIds)
        ) {
          query.push({ ...search.filters.reportsToIds, field: 'assignedSupervisorId' })
        }
      }
      if (query.length > 0 || overwrites.length > 0) {
        q.set(
          'ieq',
          btoa(
            JSON.stringify({
              query: {
                timespan:
                  (filters?.d as Timespan.Info) || timespan || userTimespan.timespan,
                filters: [
                  ...query,
                  ...overwrites.map((o) => {
                    switch (o.op) {
                      case 'eq':
                        return {
                          ...o,
                          op: 'in',
                          values: [o.value],
                        }
                      default:
                        return o
                    }
                  }),
                ],
              },
            }),
          ),
        )
      }

      return buildPath(path + `?${q.toString()}`)
    },
    [params, buildPath, search],
  )

  const navigateTo = useCallback(
    (to: unknown, options?: NavigateOptions) => {
      navigate({ to: buildPath(to), replace: options?.replace })
    },
    [navigate, buildPath],
  )

  const redirectTo = useCallback(
    (to: unknown) => {
      navigate({ to: buildPath(to), replace: true })
    },
    [navigate, buildPath],
  )

  const drilldownTo = useCallback(
    (to: unknown) => {
      navigate({ to: buildDrilldownPath(to) })
    },
    [navigate, buildDrilldownPath],
  )

  return {
    buildPath,
    buildDrilldownPath,
    buildDrilldownState,
    navigateTo,
    drilldownTo,
    redirectTo,
  }
}

export default useNavigation
